import _ from 'underscore';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { anime } from 'react-anime';

import Animation from 'components/Animation';
import Container from 'components/Container';

import Block from '../Block';
import Card from '../Card';
import MediaGrid from '../Grid';

import styles from './styles.module.scss';

const MediaRow = ({
	className,
	entities,
	theme,
	activeType,
	blockClassName,
	onClick,
	containerClassName
}) => {
	const ref = useRef(null);
	const [nowActive, setNowActive] = useState(null);
	const [, setIsClosing] = useState(false);

	const activeEntity =
		activeType && _(entities).findWhere({ type: activeType });

	const onExited = useCallback(() => {
		setIsClosing(false);
		setNowActive(activeEntity);
	}, [activeEntity]);

	useEffect(() => {
		setIsClosing((prevIsClosing) => {
			if (!activeEntity && !prevIsClosing) {
				return true;
			}

			setNowActive(activeEntity);

			return false;
		});
	}, [activeEntity]);

	const onEntered = () => {
		if (ref.current) {
			const scrollElement =
				window.document.scrollingElement ||
				window.document.documentElement ||
				window.document.body;

			const { top } = ref.current.getBoundingClientRect();

			anime({
				targets: scrollElement,
				scrollTop: window.pageYOffset + top - 60,
				duration: 500,
				easing: 'easeInOutQuad'
			});
		}
	};

	return (
		<div className={className} ref={ref}>
			<Container innerClassName={containerClassName} size="narrow">
				<MediaGrid theme={theme}>
					{entities.map((entity) => {
						const figureCx = cx(styles.MediaRowCard__Figure, {
							[styles.MediaRowCard__Figure_active]: activeEntity === entity
						});

						return (
							<div className={styles.MediaRowCard} key={entity.type}>
								<Card
									image={entity.image}
									title={entity.title}
									type={entity.type}
									active={nowActive === entity}
									onClick={onClick}
								/>
								<svg className={figureCx}>
									<path d="M8 2.62556C9.77778 -0.422061 14.2222 -0.422061 16 2.62556L24 16.3398L0 16.3398L8 2.62556Z" />
								</svg>
							</div>
						);
					})}
				</MediaGrid>
			</Container>
			<Animation
				show={!!activeEntity}
				animation="slideDown"
				onExited={onExited}
				onEntered={onEntered}
			>
				<div className={styles.MediaRowSlide}>
					<div className={styles.MediaRowSlide__Inner}>
						{nowActive && (
							<Block
								type={nowActive.type}
								containerClassName={containerClassName}
								theme="contrast"
								className={blockClassName}
								{...nowActive}
							/>
						)}
					</div>
				</div>
			</Animation>
		</div>
	);
};

MediaRow.propTypes = {
	entities: PropTypes.array.isRequired,
	className: PropTypes.string,
	onClick: PropTypes.func,
	theme: PropTypes.string,
	containerClassName: PropTypes.string,
	blockClassName: PropTypes.string,
	activeType: PropTypes.string
};

export default MediaRow;
